import { humanizeDateRange } from 'lib/helpers/dates'

/**
Returns the soft-closed peak periods for a location (the GraphQL `peakPeriods`
field already only returns soft-closed periods by default).
@returns {[Object]} peak periods ({ startDate, endDate, softClosedAt })
**/
const softClosedPeakPeriods = location =>
  (location && location.peakPeriods) || []

/**
Returns the soft-closed peak periods that actually restrict the given plan.

A plan is grandfathered (not restricted) for a period if it was registered
before that period's `softClosedAt` timestamp. When there is no existing plan
(a brand-new registration), every soft-closed period applies.
@returns {[Object]} restricting peak periods
**/
const restrictingPeriods = (location, plan) =>
  softClosedPeakPeriods(location).filter(
    period =>
      !plan ||
      !plan.registeredAt ||
      !period.softClosedAt ||
      plan.registeredAt >= period.softClosedAt
  )

/**
@returns {boolean} true if the location has any peak periods that restrict the
  given plan.
**/
const hasSoftClose = (location, plan) =>
  restrictingPeriods(location, plan).length > 0

// sort a copy of the restricting periods by start date
const sortedPeriods = (location, plan) =>
  [...restrictingPeriods(location, plan)].sort((a, b) =>
    a.startDate < b.startDate ? -1 : 1
  )

/**
@returns {[string]} human-readable restricted date ranges, e.g.
  ["Jul 6–Jul 10", "Aug 3–Aug 7"]
**/
const restrictedRanges = (location, plan) =>
  sortedPeriods(location, plan).map(period =>
    humanizeDateRange(period.startDate, period.endDate, { year: false })
  )

/**
Compute the available (eligible) date ranges: the camp's open span
(openDate..closeDate) minus the restricted peak periods.
@returns {[string]} human-readable available date ranges.
**/
const availableRanges = (location, plan) => {
  if (!location || !location.openDate || !location.closeDate) {
    return []
  }

  const oneDay = 24 * 60 * 60 * 1000
  const addDays = (iso, days) =>
    new Date(new Date(iso).getTime() + days * oneDay).toISOString().slice(0, 10)

  // start with the full open span, then subtract each restricted period
  let ranges = [{ start: location.openDate, end: location.closeDate }]

  sortedPeriods(location, plan).forEach(period => {
    ranges = ranges.flatMap(range => {
      // no overlap
      if (period.endDate < range.start || period.startDate > range.end) {
        return [range]
      }

      const result = []
      // portion before the restricted period
      if (period.startDate > range.start) {
        result.push({ start: range.start, end: addDays(period.startDate, -1) })
      }
      // portion after the restricted period
      if (period.endDate < range.end) {
        result.push({ start: addDays(period.endDate, 1), end: range.end })
      }
      return result
    })
  })

  return ranges.map(range =>
    humanizeDateRange(range.start, range.end, { year: false })
  )
}

/**
Aggregate the locations of a single session that have a current or upcoming
soft-closed peak period that restricts the given plan, grouped by region title.

Past periods (endDate < today) are excluded, as are periods that the plan is
grandfathered against (registered before the period's `softClosedAt`). A
location is omitted when it has no remaining restricting current/upcoming
periods. Scoped to a single session because a registered plan is only valid for
locations within that session.
@returns {[Object]} [{ regionTitle, locations: [{ id, name, restrictedRanges }] }]
  sorted by region title, then location name.
**/
const limitedAvailabilityByRegion = (session, plan) => {
  const locations = (session && session.locations) || []
  const today = new Date().toISOString().slice(0, 10)

  // build { regionTitle => [{ id, name, restrictedRanges }] }
  const byRegion = {}

  locations.forEach(location => {
    // keep only current/upcoming periods that actually restrict this plan
    const periods = restrictingPeriods(location, plan)
      .filter(period => period.endDate >= today)
      .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))

    if (periods.length === 0) {
      return
    }

    const restrictedRanges = periods.map(period =>
      humanizeDateRange(period.startDate, period.endDate, { year: false })
    )

    const regionTitle = location.regionTitle || ''
    byRegion[regionTitle] = byRegion[regionTitle] || []
    byRegion[regionTitle].push({
      id: location.id,
      name: location.name,
      restrictedRanges
    })
  })

  return Object.keys(byRegion)
    .sort()
    .map(regionTitle => ({
      regionTitle,
      locations: byRegion[regionTitle].sort((a, b) =>
        a.name < b.name ? -1 : 1
      )
    }))
}

export {
  softClosedPeakPeriods,
  restrictingPeriods,
  hasSoftClose,
  restrictedRanges,
  availableRanges,
  limitedAvailabilityByRegion
}
